// noinspection DuplicatedCode

import {addSeconds, format, parseISO} from 'date-fns';
import {Runner} from '../models/runner';
import {Waypoint, WAYPOINT_TYPE_CHARITY, WAYPOINT_TYPE_START} from '../models/waypoint';
import RenderProvider from './render';
import initRunnersStore, {RunnersStore} from '../store/runners';
import RunProvider, {LANGUAGE_DE} from './run';
import RunnerStatistics from './runner-statistics';

export default class PassedWaypoints {
    static instance = null;

    private runProvider: RunProvider;

    private renderProvider: RenderProvider;

    private favoriteRunners: RunnersStore;

    private unsubscribeStore: () => void;

    private readonly spinnerTemplate: string;

    private readonly rowTemplate: string;

    private passedWaypoints: Array<Waypoint>;

    private runner: Runner;

    private timeSwitchListener: () => void;

    static getInstance(): PassedWaypoints {
        if (!this.instance) {
            this.instance = new PassedWaypoints();
        }

        return this.instance;
    }

    constructor() {
        this.runProvider = RunProvider.getInstance();
        this.renderProvider = RenderProvider.getInstance();
        this.favoriteRunners = initRunnersStore();

        this.spinnerTemplate = '<div class="spinner spinner--small"><div class="spinner__element"/></div>';
        this.rowTemplate = this.renderProvider.passedWaypointsRowTemplate?.innerHTML || '';
    }

    subscribe(runner: Runner) {
        this.unsubscribe();

        if (!runner.hasStarted()) return;

        this.runner = runner;
        this.passedWaypoints = this.getPassedWaypoints(runner);
        this.renderPassedWaypoints();

        this.unsubscribeStore = this.favoriteRunners.subscribe(({favoriteRunners}) => {
            const updatedRunner = favoriteRunners.find(({startNumber}) => runner.startNumber === startNumber);

            if (updatedRunner) {
                this.runner = updatedRunner;
                this.passedWaypoints = this.getPassedWaypoints(updatedRunner);
                this.renderPassedWaypoints();
            }
        });

        this.timeSwitchListener = this.handleTimeSwitch.bind(this);
        this.renderProvider.passedWaypointsTimeSwitch.addEventListener('click', this.timeSwitchListener);
    }

    unsubscribe() {
        this.renderProvider.passedWaypoints.classList.add('hidden');

        if (this.unsubscribeStore) {
            this.unsubscribeStore();
        }

        if (this.timeSwitchListener) {
            this.renderProvider.passedWaypointsTimeSwitch.removeEventListener('click', this.timeSwitchListener);
        }
    }

    handleTimeSwitch(event: Event) {
        event.preventDefault();

        const timeSwitch = PassedWaypoints.getTimeSwitch();
        timeSwitch.dataset.status = timeSwitch.dataset.status === 'timeOfDay' ? 'default' : 'timeOfDay';

        this.renderPassedWaypoints();
    }

    static getTimeSwitch(): HTMLButtonElement {
        return document.querySelector('.js-runner-passed-waypoints-time-switch') as HTMLButtonElement;
    }

    static showTimeOfDay(): boolean {
        return PassedWaypoints.getTimeSwitch().dataset.status === 'timeOfDay';
    }

    renderPassedWaypoints() {
        if (!this.renderProvider.passedWaypointsBody) return;

        this.renderProvider.passedWaypointsBody.innerHTML = '';

        if (!this.passedWaypoints.length) return;

        this.passedWaypoints.forEach((waypoint: Waypoint) => {
            if (!waypoint?.distance) return;

            const distance: string = PassedWaypoints.convertDistanceString(waypoint.distance);
            const deltaTime: string = RunnerStatistics.displayTime(waypoint.deltaTime, true, true, true, true);
            let time: string = RunnerStatistics.displayTime(waypoint.time);

            if (PassedWaypoints.showTimeOfDay() && waypoint.time.time) {
                time = this.displayTimeOfDay(
                    addSeconds(parseISO(this.runner.startTimestamp), waypoint.time.time),
                );
            }

            let html: string = this.rowTemplate.replace('%distance%', distance);
            html = html.replace('%deltaTime%', waypoint.deltaTime.time ? deltaTime : '');
            html = html.replace('%time%', waypoint.passed ? time : this.spinnerTemplate);

            if (!waypoint.passed) {
                html = html.replace('passed-waypoints__row', 'passed-waypoints__row is-waiting');
            }

            this.renderProvider.passedWaypointsBody.insertAdjacentHTML('beforeend', html);
        });

        this.renderProvider.passedWaypoints.classList.remove('hidden');
    }

    displayTimeOfDay(value: Date): string {
        if (this.runProvider.getLanguage() !== LANGUAGE_DE) {
            return format(value, 'HH:mm');
        }

        return `${format(value, 'HH:mm')} Uhr`;
    }

    static convertDistanceString(distance: number): string {
        let distanceString: string = distance ? distance.toFixed(1)
            .toString() : '';
        if (distanceString.indexOf('.0') !== -1) {
            // eslint-disable-next-line prefer-destructuring
            distanceString = distanceString.split('.')[0];
        }

        return distanceString.replace('.', ',');
    }

    getPassedWaypoints(runner: Runner): Array<Waypoint> {
        if (!runner?.hasResults() || !runner.waypoints || runner.isOutOfTime()) {
            this.unsubscribe();
            return [];
        }

        let nextWaypoint: Waypoint;
        const passedWaypoints = runner.waypoints.filter((waypoint) => {
            if (waypoint.type !== WAYPOINT_TYPE_START && waypoint.time.time !== -1) {
                if (waypoint.passed) {
                    return true;
                }

                if (!nextWaypoint && waypoint.type !== WAYPOINT_TYPE_CHARITY) {
                    nextWaypoint = waypoint;
                }
            }

            return false;
        });

        const updatedPassedWaypoints = passedWaypoints.map((waypoint: Waypoint, index: number): Waypoint => {
            const {deltaTime} = waypoint;
            if (index !== 0) {
                deltaTime.time = waypoint.time.time - passedWaypoints[index - 1].time.time;
            } else {
                deltaTime.time = 0;
            }

            return new Waypoint({
                ...waypoint,
                deltaTime,
            });
        });

        if (!updatedPassedWaypoints.length && !nextWaypoint) {
            this.unsubscribe();
        }

        if (nextWaypoint) {
            return [...updatedPassedWaypoints, nextWaypoint];
        }

        return updatedPassedWaypoints;
    }
}
